const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common/src/shared_defs.ts';
/*
  CAMERA OVERLAY
 */

export interface IImageDataAndSrc {
    original: {
        path: string;
        width: number;
        height: number;
        size: number;
    };
    image: {
        path: string;
        width: number;
        height: number;
        size: number;
        data: string;
        type: string;
        quality: number;
    };
    preview: {
        path: string;
        width: number;
        height: number;
        size: number;
        data: string;
    };
    imgUrlPrefix: string;
    processed: string;
    addInfo?: string;
}

/*
 DEVICEINFO
 */

export type ReliableOrientation = 'LANDSCAPE' | 'PORTRAIT';
export type ExtendedReliableOrientation =
    | 'LANDSCAPE-LEFT'
    | 'LANDSCAPE-RIGHT'
    | 'LANDSCAPE'
    | 'PORTRAIT'
    | 'PORTRAIT-UPSIDEDOWN'
    | 'FACE-UP'
    | 'FACE-DOWN'
    | 'UNKNOWN';

export enum BiometryType {
    biometrics = 'Biometrics', // Android
    face = 'Face ID', // IOS
    touch = 'Touch ID', // IOS
    emulated = '[EMULATED BIOMETRIC SCAN]',
}

export interface IReliableDeviceInfo {
    orientation: ReliableOrientation;
    detailedOrientation: ExtendedReliableOrientation;
    width: number;
    height: number;
    keyboard: boolean;
    keybHeight: number;
    isTablet: boolean;
    isEmulated: boolean;
    brand: string;
    manufacturer: string;
    model: string;
    system: string;
    os: string;
    osVersion: number | string; // android yields number, ios yields version number as string
    biometryType: BiometryType | undefined;
    buildNumber: string;
}

export interface CheckCredentials {
    type: 'CHECK_USER_CREDENTIALS';
    payload: { username: string; password: string };
}

export interface DeviceInfoMsg {
    type: 'NATIVEPORT_REL_DEVICEINFO';
    payload: { info: IReliableDeviceInfo };
}

export const NATIVEPORT_REL_DEVICEINFO = 'NATIVEPORT_REL_DEVICEINFO';

export const newDeviceInfo = (info: IReliableDeviceInfo): DeviceInfoMsg => {
    return { type: NATIVEPORT_REL_DEVICEINFO, payload: { info } };
};

export interface DeviceSendInfoMsg {
    type: 'DCTRL_SENDINFO';
}

export interface DeviceLockOrientationMsg {
    type: 'DCTRL_LOCKORIENTATION';
    payload: { orientation?: ExtendedReliableOrientation };
}

export interface DeviceUnlockOrientationMsg {
    type: 'DCTRL_UNLOCKORIENTATION';
}

export interface DeviceDismissKeyboardMsg {
    type: 'DCTRL_DISMISSKEYBOARD';
}

export const DCTRL_SENDINFO = 'DCTRL_SENDINFO';
export const DCTRL_LOCKORIENTATION = 'DCTRL_LOCKORIENTATION';
export const DCTRL_UNLOCKORIENTATION = 'DCTRL_UNLOCKORIENTATION';
export const DCTRL_DISMISSKEYBOARD = 'DCTRL_DISMISSKEYBOARD';

export type DeviceControlMsg =
    | DeviceSendInfoMsg
    | DeviceLockOrientationMsg
    | DeviceUnlockOrientationMsg
    | DeviceDismissKeyboardMsg;

export const deviceSendInfo = (): DeviceSendInfoMsg => {
    return { type: DCTRL_SENDINFO };
};
export const deviceLockOrientation = (orientation?: ExtendedReliableOrientation): DeviceLockOrientationMsg => {
    return { type: DCTRL_LOCKORIENTATION, payload: { orientation } };
};
export const deviceUnlockOrientation = (): DeviceUnlockOrientationMsg => {
    return { type: DCTRL_UNLOCKORIENTATION };
};
export const deviceDismissKeyboard = (): DeviceDismissKeyboardMsg => {
    return { type: DCTRL_DISMISSKEYBOARD };
};

export const ANDROID_BACK = 'ANDROID_BACK';

export interface AndroidBackButtonPressed {
    type: 'ANDROID_BACK';
}

// PIN unlock: the native lock screen delegates the PIN check to the web, which validates
// it online (KISIM UserPinCheck) so a stale OAuth token is detected at unlock time instead
// of hanging on the first real KISIM call. CHECK_PIN_ONLINE is native -> web (the entered
// PIN); PIN_UNLOCK_RESULT is web -> native (the outcome).
export const CHECK_PIN_ONLINE = 'CHECK_PIN_ONLINE';
export const PIN_UNLOCK_RESULT = 'PIN_UNLOCK_RESULT';

/**
 * - `unlock`: PIN accepted and the session is still valid -> dismiss the lock screen.
 * - `denied`: PIN did not match -> let the native lock screen show its retry/error UI.
 * - `expired`: the session token is no longer valid -> the web forces a re-login; native
 *   just has to reveal the WebView so that re-login screen becomes visible.
 */
export type PinUnlockResult = 'unlock' | 'denied' | 'expired';

export interface CheckPinOnlineMsg {
    type: 'CHECK_PIN_ONLINE';
    payload: { pin: string };
}

export interface PinUnlockResultMsg {
    type: 'PIN_UNLOCK_RESULT';
    payload: { result: PinUnlockResult };
}

// Gestures

export interface GestureMode {
    pinchZoom: 'all' | 'final' | 'off';
    longPress: 'final' | 'off';
}

export const initialGestureModeValues: GestureMode = {
    pinchZoom: 'off',
    longPress: 'off',
};

export interface GestureEnableMsg {
    type: 'GESTURE_ENABLE';
    payload: Partial<GestureMode>;
}

export interface GestureDisableMsg {
    type: 'GESTURE_DISABLE';
}

export type GestureControlMsg = GestureEnableMsg | GestureDisableMsg;

export interface GesturePinchZoomEventMsg {
    type: 'GESTURE_PINCH';
    payload: {
        type: 'update' | 'final';
        scale: number;
    };
}

export interface GestureLongPressEventMsg {
    type: 'GESTURE_LONG';
    payload: {
        type: 'final';
        x: number;
        y: number;
    };
}

export type GestureEventMsg = GestureLongPressEventMsg | GesturePinchZoomEventMsg;

// New camera defs

export interface RelativePositionAndSize {
    top?: number;
    left?: number;
    bottom?: number;
    right?: number;
    width?: number;
    height?: number;
}

export interface CompleteRelativePositionAndSize {
    top: number;
    left: number;
    width: number;
    height: number;
}

export interface PhotoParameters {
    maxWidth: number;
    maxHeight: number;
    allowUpscale: boolean;
    jpgQuality: number;
}

export interface CameraButtonColors {
    front: string;
    back: string;
}

export interface CameraMsgOptions {
    position?: RelativePositionAndSize;
    torchButton?: 'show' | 'hide';
    torchButtonPosition?: RelativePositionAndSize;
    torchButtonColors?: CameraButtonColors;
    flashButtonPosition?: RelativePositionAndSize;
    flashButtonColors?: CameraButtonColors;
    cancelButton?: 'show' | 'hide';
    cancelButtonPosition?: RelativePositionAndSize;
    cancelButtonColors?: CameraButtonColors;
    photoButton?: 'show' | 'hide';
    photoButtonPosition?: RelativePositionAndSize;
    photoButtonColors?: CameraButtonColors;
    switchButton?: 'show' | 'hide';
    switchButtonPosition?: RelativePositionAndSize;
    switchButtonColors?: CameraButtonColors;
    background?: 'transparent' | 'dark' | 'black' | 'off';
    keepCameraOpen?: boolean;
    infoText?: string | null;
    infoTextSize?: number;
    infoTextColor?: string;
    infoBoxColor?: string | null;
    statusBarColor?: string;
    deliverOriginalImage?: boolean;
    photoParameters?: PhotoParameters;
    vibrateOnBarcodeScan?: boolean;
    soundOnBarcodeScan?: boolean;
    tapToScan?: boolean;
    resultNeedsRotateTo?: number;
    type?: string;
    codeCondition?: CodeCondition;
}
export interface CodeCondition {
    types: CodeConditionType;
    pattern: string;
}
export type CodeConditionType = Array<keyof typeof barCodeTypeMap>;

export const barCodeTypeMap = {
    itf: { ios: 'itf', android: 'itf' },
    dataMatrix: { ios: 'data_matrix', android: 'data-matrix' },
    aztec: { ios: 'aztec', android: 'aztec' },
    code128: { ios: 'code_128', android: 'code-128' },
    code39: { ios: 'code_39', android: 'code-39' },
    code93: { ios: 'code_93', android: 'code-93' },
    codabar: { ios: 'codabar', android: 'codabar' },
    ean13: { ios: 'ean_13', android: 'ean-13' },
    ean8: { ios: 'ean_8', android: 'ean-8' },
    pdf417: { ios: 'pdf_417', android: 'pdf-417' },
    qr: { ios: 'qr', android: 'qr' },
    upcE: { ios: 'upc_e', android: 'upc-e' },
    upcA: { ios: 'upc_a', android: 'upc-a' },
    all: { ios: 'all', android: 'all' },
} as const;

export type CameraMsgType = 'CAMNEWMSG';
export type CameraMsgSignals =
    | 'START_BC'
    | 'START_CAM'
    | 'ACTIVATE_MYCO'
    | 'STOP'
    | 'PHOTO'
    | 'GETINFO'
    | 'SELECT_FRONT'
    | 'SELECT_BACK'
    | 'TOGGLE_CAM'
    | 'TORCH_ON'
    | 'TORCH_OFF'
    | 'TOGGLE_TORCH'
    | 'FLASH_ON'
    | 'FLASH_OFF'
    | 'TOGGLE_FLASH';

export interface CameraMessage {
    type: CameraMsgType;
    signal?: CameraMsgSignals;
    options?: CameraMsgOptions;
}

export type CameraEventType = 'CAMNEWEVENT';
export type CameraEventSignals =
    | 'STARTED_BC'
    | 'STOPPED_BC'
    | 'BARCODE'
    | 'STARTED_CAM'
    | 'STOPPED_CAM'
    | 'PHOTO'
    | 'INFO';

export interface CameraEvent {
    type: 'CAMNEWEVENT';
    signal: CameraEventSignals;
    payload?: any;
}
